<template>
  <header
    :id="header.id"
    class="header-container"
    :class="{
      rtl: dir === 'rtl'
    }"
  >
    <!-- ------------------- -->
    <!-- UTILITY BAR DESKTOP -->
    <!-- ------------------- -->
    <div v-if="desktopOrTablet" class="utility-bar-area">
      <div
        class="header-max-width utility-bar-body"
        :class="[utilityBarCenterClass]"
      >
        <!-- TOP LEFT -->
        <!-- -------- -->
        <div class="utility-bar-item top-left">
          <!-- REGION SELECTOR -->
          <NsMinimalHeaderRegionSelector
            class="ns-minimal-region-selector"
            :href-override="header.homeUrl"
            :region="header.region"
            :region-selector-path="header.regionSelectorPath"
            :translations="header.translations"
          />
        </div>

        <!-- TOP RIGHT -->
        <!-- --------- -->
        <div class="utility-bar-item top-right"></div>
      </div>
    </div>

    <!-- ------------- -->
    <!-- HEADER BODY -->
    <!-- ------------- -->
    <div class="header-body">
      <div class="header-layout header-max-width">
        <!-- HEADER LEFT -->
        <!-- ----------- -->
        <div class="header-left">
          <!-- LOGO -->
          <div class="header-left-two">
            <div
              v-if="header.showDistributorImage"
              class="storefront-header-logo"
            >
              <NsMinimalHeaderLogo
                v-if="!header.distributorImage"
                :link-url="header.homeUrl"
                no-simple-logo
                small
                :mystore="false"
              />
              <a id="homeLink" :href="header.homeUrl">
                <img
                  v-if="header.distributorImage"
                  id="headerLogoImage"
                  :src="header.distributorImage"
                  alt="Profile Image"
                  class="mec-avatar is-square"
                />
              </a>
            </div>
            <NsMinimalHeaderLogo
              v-if="!header.showDistributorImage"
              :link-url="header.homeUrl"
              no-simple-logo
              small
            />
          </div>
        </div>

        <!-- HEADER RIGHT -->
        <!-- ------------ -->
        <div class="header-right">
          <!-- PROFILE -->
          <div class="header-right-one">
            <NsMinimalHeaderProfileToggle
              :greeting="profileToggleTranslations.greeting"
              :sign-in-label="profileToggleTranslations.signInLabel"
              :sign-up-label="profileToggleTranslations.signUpLabel"
              :sign-out-label="profileToggleTranslations.signOutLabel"
              :sign-up-href="header.signUpUrl"
              :sign-out-url="header.signOutUrl"
              :profile-url="header.profileUrl"
            />
          </div>

          <!-- CART -->
          <div class="header-right-two">
            <NsMinimalHeaderMiniCart
              class="ns-mini-cart"
              :shopping-bag-text="header.cartText"
              :show-shopping-bag-text="header.showCartText"
            />
          </div>
          <!-- MENU BUTTON -->
          <div class="header-right-three">
            <div class="menu-button" @click="toggleMenu">
              <NsIcon icon-name="icon-hamburger" />
            </div>
          </div>
        </div>
      </div>

      <!-- HEADER SECOND ROW -->
      <NsMinimalHeaderTopnav
        class="header-max-width header-divider max-width-only"
      />
    </div>

    <!-- ------------- -->
    <!-- MOBILE DRAWER -->
    <!-- ------------- -->
    <NsMinimalDrawer
      v-if="!desktop"
      :open="mobileDrawerOpen"
      width="300"
      @close="mobileDrawerOpen = false"
    >
      <NsMinimalHeaderDrawer
        :region="header.region"
        :href-override="header.homeUrl"
        :region-selector-path="header.regionSelectorPath"
        :signup-href="header.signUpUrl"
        :translations="headerDrawerTranslations"
        :join-us="header.joinUs"
        :legal-links="header.legalLinks"
        @close-drawer="mobileDrawerOpen = false"
      >
        <template #header>
          <NsMinimalHeaderProfileDropdownMobile
            v-if="!desktop"
            :profile-menu="header.profileMenu"
            :sign-out-url="header.signOutUrl"
            :sign-up-url="header.signUpUrl"
            :profile-url="header.profileUrl"
            :translations="header.translations"
            :profile-translations="profileToggleTranslations"
            @close-drawer="mobileDrawerOpen = false"
          />
        </template>
      </NsMinimalHeaderDrawer>
    </NsMinimalDrawer>
  </header>
</template>

<script>
import { events, DirUtil } from "@nuskin/ns-util";
import { MarketHeaderService } from "@nuskin/ns-product";
import { NsIcon } from "@nuskin/design-components";
import { isNullOrEmpty } from "@nuskin/ns-common-lib";

import NsMinimalHeaderRegionSelector from "./MinimalHeader/NsMinimalHeaderRegionSelector.vue";
import NsMinimalHeaderProfileDropdownMobile from "./MinimalHeader/NsMinimalHeaderProfileDropdownMobile.vue";
import NsMinimalHeaderLogo from "./MinimalHeader/NsMinimalHeaderLogo.vue";
import NsMinimalHeaderMiniCart from "./MinimalHeader/NsMinimalHeaderMiniCart.vue";
import NsMinimalHeaderTopnav from "./MinimalHeader/NsMinimalHeaderTopnav.vue";
import NsMinimalDrawer from "./MinimalHeader/NsMinimalDrawer.vue";
import NsMinimalHeaderDrawer from "./MinimalHeader/NsMinimalHeaderDrawer.vue";
import NsMinimalHeaderProfileToggle from "./MinimalHeader/NsMinimalHeaderProfileToggle.vue";
import vueEventBus from "./EventBus";

const DESKTOP_MAX_MARKETING_LENGTH = 100;

export default {
  name: "NsMinimalHeader",
  components: {
    NsIcon,
    NsMinimalHeaderRegionSelector,
    NsMinimalHeaderProfileDropdownMobile,
    NsMinimalHeaderProfileToggle,
    NsMinimalHeaderLogo,
    NsMinimalHeaderMiniCart,
    NsMinimalHeaderTopnav,
    NsMinimalDrawer,
    NsMinimalHeaderDrawer
  },
  data() {
    return {
      logoData: {},
      marketData: {},
      miniCartData: {},
      mobileDrawerOpen: false,
      profileData: {},
      searchResultsPageData: {},
      searchbarData: {},
      showMobileMarketingMessage: true,
      showSearchResults: false
    };
  },
  computed: {
    desktop() {
      return this.$mq === "desktop" || this.$mq === "desktopHD";
    },
    desktopOrTablet() {
      return this.desktop || this.$mq === "tablet";
    },
    market() {
      return (
        this.$NsAppService.runConfig.market ||
        this.$NsAppService.runConfig.country ||
        ""
      );
    },
    language() {
      return this.$NsAppService.runConfig.language || "";
    },
    locale() {
      return this.$NsAppService.locale || "";
    },
    baseUrl() {
      return this.$NsAppService.runConfig.baseUrl || "";
    },
    isStorefront() {
      return !!this.$NsAppService.isStorefront;
    },
    profileToggleTranslations() {
      if (this.profileData && this.profileData.translations) {
        return {
          greeting: this.profileData.translations.greeting,
          signInLabel: (this.profileData.translations.signIn || {}).name,
          signUpLabel: this.profileData.translations.signUp,
          signOutLabel: (this.profileData.translations.signOut || {}).name
        };
      }
      return {};
    },
    headerDrawerTranslations() {
      return {
        ...this.translations,
        ...this.profileToggleTranslations
      };
    },
    header() {
      return {
        //The URL for a distributor profile image that is shown in place of the NS Logo
        distributorImage:
          this.logoData.distributorImage ||
          this.marketData.distributorImage ||
          "",
        homeUrl: this.logoData.linkUrl || this.marketData.hrefOverride || "",
        id: this.marketData.id || "ns-header",
        joinUs: this.marketData.joinUs || {
          label: "Join",
          link: "/join.html"
        },
        accessibilityLink: this.marketData.accessibility || {
          showAccessibilityLink: false,
          accessibilityActivate: "Activate Accessibility",
          accessibilityDeactivate: "Turn Off Accessibility",
          accessibilityHref: "#",
          accessibilityStyle: "UsableNetAssistive",
          accessibilityOnclick: "return enableUsableNetAssistive()"
        },
        legalLinks: this.marketData.legalLinks || [],
        marketingMessage: this.marketData.marketingMessage || {
          info: "",
          href: "",
          link: ""
        },
        region: this.marketData.region || {},
        regionSelectorPath:
          this.marketData.regionSelectorPath ||
          "/content/nuskin.html#region-selection",
        translations: this.marketData.translations || {
          selectOther: "Select Other"
        },
        cartText: this.miniCartData.shoppingBagText || "",
        showCartText: this.miniCartData.showShoppingBagText || false,
        // profile
        profileMenu: this.profileData.profileMenu || [],
        signOutUrl: (() => {
          const translations = this.profileData.translations || {};
          return (translations.signOut && translations.signOut.href) || "";
        })(),
        signUpUrl: this.profileData.signUpUrl || "",
        profileUrl:
          (this.profileData.profileMenu &&
            this.profileData.profileMenu[0].href) ||
          "",
        // storefront
        showDistributorImage:
          this.logoData.showDistributorImage ||
          this.marketData.showDistributorImage ||
          false
      };
    },
    dir() {
      return DirUtil.getDir();
    },
    utilityBarCenterClass() {
      const { info = "", link = "" } = this.header.marketingMessage || {};

      if (this.header.marketingMessage) {
        return info.length + link.length > DESKTOP_MAX_MARKETING_LENGTH
          ? "unlock-center"
          : "";
      } else {
        return "";
      }
    }
  },
  async mounted() {
    this.addListeners();
    this.getPropsFromAjax();
  },
  destroyed() {
    this.removeListeners();
  },
  methods: {
    closeMarketingMessage() {
      this.showMobileMarketingMessage = false;
    },

    async getPropsFromAjax() {
      let page = window.location.pathname.replace("#/", "");
      if (!page.includes(this.locale)) {
        page = "/content/";
        if (this.market.toLowerCase() === "jp") {
          page += "markets";
        } else {
          page += "nuskin";
        }
        page += `/${this.locale}/home.html`;
      } else {
        // Make sure JP is using content/markets path and not just /markets
        page =
          page.indexOf("content/markets/") > -1
            ? page
            : page.replace("/markets/", "/content/markets/");
      }

      const siteUrl = window.location.origin.startsWith("http://localhost")
        ? this.baseUrl || "https://test.nuskin.com"
        : window.location.origin;

      const headerData = await MarketHeaderService.getMarketHeaderData(
        page,
        siteUrl,
        false
      );

      if (isNullOrEmpty(this.logoData)) {
        this.logoData = headerData.logo || {};
      }

      this.marketData = headerData.market || {};
      this.miniCartData = headerData.miniCart || {};
      this.profileData = headerData.profile || {};
      this.searchResultsPageData = headerData.searchResultsPage || {};
      this.searchbarData = headerData.searchbar || {};
    },

    getOffSetHeight() {
      const domHeader = document.querySelector("#" + this.header.id);
      return domHeader ? domHeader.offsetHeight : 0;
    },

    /**
     * Subscribe to events, should only be called when mounted
     */
    addListeners() {
      window.addEventListener("resize", this.handleWindowResize);

      this.$watch(
        function() {
          return !this.$NsAppService.loading;
        },
        this.getPropsFromAjax,
        {
          immediate: true
        }
      );

      events.subscribe(
        events.global.RUN_CONFIG_CHANGED,
        this.$NsAppService.checkConfig
      );

      vueEventBus.$on("config-changed", this.handleConfigChanged);
    },

    /**
     * Unsubscribe from events, should only be called when destroyed
     */
    removeListeners() {
      window.removeEventListener("resize", this.handleWindowResize);

      events.unsubscribe(
        events.global.RUN_CONFIG_CHANGED,
        this.$NsAppService.checkConfig
      );

      vueEventBus.$off();
    },
    toggleMenu() {
      this.mobileDrawerOpen = !this.mobileDrawerOpen;
      events.publish("toggleDrawer");
    },

    handleConfigChanged() {
      this.getPropsFromAjax();
    },

    handleWindowResize() {
      setTimeout(() => {
        let offsetHeight = this.getOffSetHeight();
        document.documentElement.style.setProperty(
          "--header-height",
          `${offsetHeight}px`
        );
      }, 1);
    }
  }
};
</script>

<style lang="scss" scoped>
@use "sass:map";
@use "~@nuskin/ns-core-styles/src/scss/colors";
@use "~@nuskin/ns-core-styles/src/scss/other";
@use '~@nuskin/ns-core-styles/src/scss/global.mixins';
@use '~@nuskin/ns-core-styles/src/scss/typographyVariables';
@use "~@nuskin/ns-core-styles/src/scss/globalVariables";

$headerPadding: 15px;
$headerBoxShadow: 0 2px 4px 0 rgba(0, 0, 0, 0.15);

.ns-header {
  position: relative;
  width: 100%;
  box-sizing: border-box;

  * {
    box-sizing: border-box;
  }
}

/*LAYOUT*/
header.header-container {
  position: relative;
  width: 100%;
  margin: 0 auto;
  background-color: white;
  font-family: map.get(typographyVariables.$typography, font-family-primary);
  user-select: none;
  z-index: map.get(other.$otherUtils, z-header);
  box-shadow: $headerBoxShadow;
}

.header-search-results {
  display: none;
  position: relative;
  width: 100%;
  flex: 1;
  font-family: map.get(typographyVariables.$typography, font-family-primary);
  background-color: map.get(colors.$themeColors, color-background-1);
  overflow-y: auto;
}

/*LAYOUT*/
header.header-container {
  position: relative;
  background-color: white;
  font-family: map.get(typographyVariables.$typography, font-family-primary);
  user-select: none;
  z-index: map.get(other.$otherUtils, z-header);
  box-shadow: $headerBoxShadow;
}

.header-body {
  position: relative;
  padding: 10px;
  background-color: white;
  z-index: map.get(other.$otherUtils, z-header);
}

.header-layout {
  display: grid;
  grid-template-areas: "left center right";
  grid-gap: calc(#{$headerPadding} - 5px); // 10px
  grid-template-columns: auto 1fr auto;
}

.header-max-width {
  max-width: 100%;
  width: 100%;
  margin-left: auto;
  margin-right: auto;
}

.header-divider {
  border-top: solid black 1px;
}

.header-left {
  grid-area: left;
  display: grid;
  grid-template-areas: "leftOne leftTwo";
  align-items: center;
  grid-column-gap: $headerPadding;
}

.header-left ::slotted(*) {
  justify-self: flex-start;
}

.header-right {
  grid-area: right;
  display: grid;
  grid-template-areas: "rightOne rightTwo rightThree";
  align-items: center;
}

.header-right-two {
  grid-area: rightTwo;
  & .ns-mini-cart {
    display: block;
    margin: 0 10px;
    text-align: right;
  }
  .ns-minimal-region-selector {
    display: block;
    margin: 0 10px;
    text-align: right;
  }
}

.header-left-one {
  grid-area: leftOne;
}

.header-left-two {
  grid-area: leftTwo;
  display: flex;
  justify-content: flex-start;
  align-items: center;
  height: 100%;
}
/*END LAYOUT*/

/*LOGO*/
.mec-avatar {
  width: 60px;
  height: 60px;
  border-radius: 50%;
  border: 1px solid #272525;
}
/* line 131, ../scss/_main.scss */
.mec-avatar.is-square {
  border-radius: 0;
  width: auto;
  border: none;
}
@media (max-width: 1023px) {
  /* line 125, ../scss/_main.scss */
  .mec-avatar {
    margin-left: 5px;
  }
}
@media (max-width: 767px) {
  /* line 125, ../scss/_main.scss */
  .mec-avatar {
    margin-left: 10px;
  }

  .max-width-only {
    display: none;
  }
}

/*HAMBURGER MENU ANIMATION*/
.menu-button {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;

  padding: 10px;
  padding-left: 0px;
  padding-right: 5px;
  width: 35px;
  height: 40px;
  color: map.get(colors.$themeColors, primary-text-color);
  cursor: pointer;
}

/* TOP BAR */
.utility-bar-area {
  border: none;
  min-height: 45px;
}

.utility-bar-body {
  display: grid;
  grid-template-columns: repeat(1, 1fr);
  align-items: center;
  min-height: 45px;
}

.utility-bar-area .top-right {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  height: inherit;
}

.utility-bar-area .top-center {
  overflow: hidden;
}

.textblock {
  overflow: hidden;
  position: relative;
  line-height: 1.2em;
  max-height: 2.4em;
  padding-right: 40px;
  padding-left: 1em;
}

.textblock::before {
  content: "";
  position: absolute;
  right: 20px;
  bottom: 0;
}

.mobile-full-link {
  position: absolute;
  top: 0;
  left: 0;
  width: calc(100% - 40px);
  height: 100%;
}

.join-link {
  text-decoration: none;
  border-bottom: 1px solid transparent;
  &:hover {
    border-bottom: 1px solid currentColor;
  }
}

// NS PROFILE
.utility-bar-area .ns-profile {
  margin: 0 !important;
  margin-left: 15px !important;
  padding: 0 !important;
}

.profile__close {
  display: flex;
  align-items: center;
  justify-content: center;
  border-left: 1px solid rgba(0, 0, 0, 0.15);
  width: 60px;
  height: 60px;
  cursor: pointer;
}

.profile__close-icon {
  width: 14px;
  height: 14px;
  color: var(--color-interactive-7);
}

@media (min-width: 768px) {
  .utility-bar-area {
    padding: 0 15px;
    border-bottom: 1px solid rgb(236, 237, 237);
    min-height: 30px;
  }

  .utility-bar-body {
    grid-template-columns: 1fr auto 1fr;
    gap: 30px;
    min-height: 30px;

    &.unlock-center {
      display: flex;
      justify-content: space-between;
      // some browsers support flex gap, but for now we'll do a work-around for better consistancy
      gap: 0px;

      // css magic to add spacing between flex items and to account for empty items
      > :not(:first-child):not(:empty) {
        margin-left: 30px;
      }

      > :empty + :not(:empty) {
        margin-left: 0px;
      }

      .top-left:empty {
        display: none;
      }
    }
  }

  .rtl .utility-bar-area ::slotted(.ns-profile),
  .rtl .utility-bar-area .ns-profile {
    margin-left: auto;
    margin-right: 20px;
    padding-left: 0;
    padding-right: 20px;
  }

  .utility-bar-area .ns-profile {
    margin-left: 15px !important;
    padding: 0 !important;
  }

  .header-body {
    padding: 8px 15px;
  }

  .header-left,
  .header-right {
    align-items: center;
  }

  .header-right {
    grid-template-areas: "rightOne rightTwo";
    justify-content: flex-end;
  }

  .header-left {
    grid-template-areas: "leftTwo";
  }

  .header-left-one {
    display: none;
  }

  .header-right .menu-button {
    display: none;
  }

  .mobile-full-link {
    display: none;
  }
}

@media (min-width: 1440px) {
  .header-max-width {
    width: 1410px;
  }
}

a[href=".html"] {
  display: none;
}
</style>
